import React, { useMemo, useState } from 'react';
import { Alert, OverlayTrigger, Tab, Tabs, Tooltip } from 'react-bootstrap';
import { EventKey } from 'react-bootstrap/esm/types';
import { PromotionSubcategory } from '../../types/PromotionTypes';
import styles from './PromotionTypeTabs.module.scss';

interface PromotionTypeTabLabelProps {
  subcategory: PromotionSubcategory;
  configured: boolean;
  isActive: boolean;
  dirty: boolean;
}

function PromotionTypeTabLabel(props: PromotionTypeTabLabelProps) {
  const { subcategory, configured, isActive, dirty } = props;
  const showConfiguredPill = configured && !isActive;
  const titleClass = showConfiguredPill
    ? `${styles.tabTitle} ${styles.tabTitleConfigured}`
    : styles.tabTitle;

  return (
    <span className={styles.tabLabel}>
      <span className={titleClass}>
        {subcategory.title}
        {dirty && (
          <OverlayTrigger
            placement="top"
            overlay={(
              <Tooltip id={`unsaved-changes-${subcategory.key}`}>
                You have unsaved changes.
              </Tooltip>
            )}
          >
            <span className={styles.dirtyBadge} aria-label="Unsaved changes">!</span>
          </OverlayTrigger>
        )}
      </span>
      <span className={styles.tabSubtitle}>
        {subcategory.tabSubtitle ?? '\u00A0'}
      </span>
    </span>
  );
}

function defaultTabPanel(subcategory: PromotionSubcategory) {
  return (
    <Alert variant="info" className="mb-0 mt-3">
      The
      {' '}
      {subcategory.title}
      {' '}
      editor is not implemented yet. Configuration can be added here in a later phase.
    </Alert>
  );
}

export interface PromotionTypeTabsProps {
  subcategories: PromotionSubcategory[];
  configuredTags: string[];
  dirtyKeys?: string[];
  dimmed?: boolean;
  renderTabPanel?: (subcategory: PromotionSubcategory) => React.ReactNode;
}

export function PromotionTypeTabs(props: PromotionTypeTabsProps) {
  const {
    subcategories,
    configuredTags,
    dirtyKeys = [],
    dimmed = false,
    renderTabPanel = defaultTabPanel,
  } = props;

  const [activeKey, setActiveKey] = useState<EventKey | null>(null);

  const configuredSet = useMemo(() => new Set(configuredTags), [configuredTags]);
  const dirtySet = useMemo(() => new Set(dirtyKeys), [dirtyKeys]);

  const handleSelect = (key: EventKey | null) => {
    if (key == null || key === '') {
      setActiveKey(null);
      return;
    }
    setActiveKey((current) => (current === key ? null : key));
  };

  if (subcategories.length === 0) {
    return null;
  }

  return (
    <div className={dimmed ? `${styles.tabs} ${styles.tabsDimmed}` : styles.tabs}>
      <Tabs
        id="promotion-type-tabs"
        activeKey={activeKey ?? ''}
        onSelect={handleSelect}
      >
      {subcategories.map((subcategory) => (
        <Tab
          key={subcategory.key}
          eventKey={subcategory.key}
          mountOnEnter
          title={(
            <PromotionTypeTabLabel
              subcategory={subcategory}
              configured={configuredSet.has(subcategory.key)}
              isActive={activeKey === subcategory.key}
              dirty={dirtySet.has(subcategory.key)}
            />
          )}
        >
          <div className={styles.formPanel}>
            {renderTabPanel(subcategory) ?? defaultTabPanel(subcategory)}
          </div>
        </Tab>
      ))}
      </Tabs>
    </div>
  );
}
